import { dateFormat, getAge } from "../../core/utils/date-utils";
import { Button, Card, Checkbox, Col, Divider, Form, Input, Modal, Popover, Row, Select, Space, Tooltip, Typography } from "antd";
import ReportEditor from "../../core/components/ReportEditor";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import ApiService from "../../core/service/api-service";
import Flex from "../../core/components/Flex";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import { useApp } from "../../core/context/AppContext";
import useRequest from "../../core/utils/useRequest";
import { studyTaskTypeKey } from "../../enums/StudyTaskTypeEnum";
import TaskResolutionField, { OpenTasksBox } from "../study/TaskResolutionField";
import { hasBoldText, hasReportText } from "../../core/components/report-content";

// Patolojik bulgu, Bulgular bölümünden türetilir: hekim patolojik bulguyu kalın yazar.
// Bölüm boşken karar verilemez — "belirtilmemiş" (null) kalır.
//
// Hesap hiçbir koşulda kaydetmeyi bloklamaz: bozuk/beklenmedik HTML'de hata yutulur, log basılır
// ve false ile devam edilir. Rapor metnini kaybetmektense yanlış bir bayrak yazmak yeğdir; değer
// zaten yetkili kullanıcı tarafından önizlemeden düzeltilebiliyor.
const derivePathologicalFinding = (bulgular) => {
    try {
        return hasReportText(bulgular) ? hasBoldText(bulgular) : null;
    }
    catch (e) {
        console.error('Patolojik bulgu hesaplanamadı, false olarak devam ediliyor', e)
        return false;
    }
}

export default function ReportCreateEditForm({ studyId, request, onSaved, title }) {
    const { t } = useTranslation();
    const { hasPermission } = useUser()
    const { addKeyEvent, removeKeyEvent, pressedKey } = useApp()
    const formRef = useRef();
    const [autoClose, setAutoClose] = useState(localStorage.getItem('autoClose') || false)
    const [confirmModalVisible, setConfirmModalVisible] = useState(false)
    const [confirmForm] = Form.useForm()

    const [_reportId, setReportId] = useState(request?.request?.reportId)
    const [readUsers, setReadUsers] = useState([])
    const [templates, setTemplates] = useState([])
    const [template, setTemplate] = useState({ content: {} });
    const [reportContent, setReportContent] = useState({});
    const [report, setReport] = useState({
        request: {
            requestType: {}
        }
    })
    const reportSaveContent = useRef({})

    const getReadUsers = async () => {
        const res = await ApiService.StudyDetails.GetReadUsersOfStudy([studyId]);
        if (res.success) {
            setReadUsers(res.data)
        }
    }

    const loadReportFileData = async (studyId, requestId, reportId) => {
        try {
            const response = await ApiService.Report.GetReportFileData(studyId, requestId, reportId);
            if (response.success) {
                const content = JSON.parse(response.data);
                setReportContent(content)
                reportSaveContent.current = content;
                return content;
            }
        }
        catch (exp) {
            console.log(exp)
            setReportContent({})
        }

        return {};
    }

    const onTemplateChange = async (val, option) => {
        setTemplate(option)
        await loadReportTemplateFile(option.id);
    }

    const loadReportTemplateFile = async (templateId) => {
        try {
            if (templateId) {
                const response = await ApiService.ReportTemplate.GetReportTemplateFileData(templateId);
                if (response.success) {
                    const content = JSON.parse(response.data);
                    setReportContent(content)
                    reportSaveContent.current = content
                    return;
                }
            }

            setReportContent({})
        }
        catch (e) {
            setReportContent({})
        }
    }

    const loadReport = async (requestId, reportId) => {
        if (request?.id && request?.reportId) {
            const response = await ApiService.Report.Get(studyId, requestId, reportId)
            if (response.success) {
                setTemplate({})
                setReport(response.data)
                await loadReportFileData(studyId, requestId, reportId)
            }
        }
    }

    // Çekimin EN SON okuması. Dizi konumuna (readUsers[length-1]) güvenmek yerine readDate'e
    // bakılır: sunucu artık ReadDate'e göre sıralı dönüyor ama varsayılanın hangi okuma olduğu
    // ekranda görünmeyen bir sıralamaya bağlı kalmamalı — yanlış hekim seçili gelirse rapor
    // yanlış okuyana yazılır.
    const getLastReadUser = (list) => {
        if (!list?.length)
            return null;

        return list.reduce((latest, x) =>
            new Date(x?.readDate || 0) >= new Date(latest?.readDate || 0) ? x : latest
        );
    }

    useEffect(() => {
        // Kayıtlı raporun okuyanı her zaman kazanır: mevcut bir rapor açılıyorsa seçim
        // değiştirilmemeli, yoksa kaydetmede okuyan sessizce değişirdi.
        if (report?.studyReadUser?.id) {
            formRef.current.setFieldsValue({
                studyReadUserId: report?.studyReadUser?.id
            })
            return
        }

        const lastRead = getLastReadUser(readUsers)
        if (lastRead?.id) {
            formRef.current.setFieldsValue({
                studyReadUserId: lastRead.id
            })
        }
        // `report` de bağımlılık: init() içinde önce getReadUsers, SONRA loadReport bitiyor.
        // Yalnızca [readUsers] olsaydı rapor yüklendiğinde efekt yeniden çalışmaz ve mevcut
        // raporu açarken kayıtlı okuyan yerine "son okuma" seçili kalırdı.
    }, [readUsers, report])

    const previewPdf = async () => {
        const response = await ApiService.ReportGenerator.GetReportPreviewPdf(studyId, request?.id, request?.reportId, {
            ...reportSaveContent.current
        })

        if (response.success) {
            console.log(response.data)
            const pdfBase64 = response.data.pdfFile;
            const pdfWindow = window.open("");
            pdfWindow.document.write(
                `<iframe width='100%' height='100%' src='data:application/pdf;base64,${pdfBase64}'></iframe>`
            );
        }
    }

    const save = async (isConfirm, confirmDescription = null) => {
        if (!request?.id || request?.reportIsConfirm)
            return

        if (!hasPermission(PERMISSIONS.REPORT_CONFIRM) && isConfirm)
            return

        const text = formRef.current.getFieldsValue().text;
        const studyReadUserId = formRef.current.getFieldsValue().studyReadUserId;
        // Patolojik bulgu YALNIZCA kaydederken hesaplanır. Rapor yazan kullanıcı bu alanı görmez;
        // yetkili kullanıcı rapor önizlemesinden elle değiştirebilir (o zaman isManual=true olur).
        const hasPathologicalFinding = derivePathologicalFinding(reportSaveContent.current?.Bulgular);

        if (text)
            await ApiService.StudyAnamnez.EditClinicalInformation(studyId, text)

        if (request?.reportId) {
            const response = await ApiService.Report.Edit(studyId, request?.id, request?.reportId, {
                content: reportSaveContent.current,
                studyReadUserId,
                isConfirm,
                language: template.language || report?.language,
                description: confirmDescription,
                hasPathologicalFinding
            })

            response.success && onSaved && onSaved(studyId, request?.id, response.data);
            await loadReport(request?.id, response.data)
        } else {
            const response = await ApiService.Report.Create(studyId, request?.id, {
                content: reportSaveContent.current,
                studyReadUserId,
                isConfirm,
                language: template.language || report?.language,
                description: confirmDescription,
                hasPathologicalFinding
            })

            response.success && onSaved && onSaved(studyId, request?.id, response.data);
            await loadReport(request?.id, response.data)
        }
    }

    // Kaydetme sırasında geri bildirim tüm ekranı karartmak yerine butonun kendisinde verilir.
    // antd Button `loading` iken tıklamayı yok saydığı için bu aynı zamanda çift kaydı da engeller.
    // İki ayrı sarmalayıcı: "Kaydet"e basınca yalnız o buton döner, "Kaydet ve Onayla" değil.
    const {run: runSave, loading: saving} = useRequest(save)
    const {run: runSaveAndConfirm, loading: confirming} = useRequest(save)
    // Önizleme PDF'i sunucuda üretiliyor, birkaç saniye sürebiliyor.
    const {run: runPreviewPdf, loading: previewing} = useRequest(previewPdf)

    const handleSaveAndConfirmClick = async () => {
        if (request?.studyTasks && request.studyTasks.length > 0) {
            setConfirmModalVisible(true);
        } else {
            await runSaveAndConfirm(true);
        }
    }

    const handleConfirmWithDescription = async () => {
        try {
            const values = await confirmForm.validateFields();
            await runSaveAndConfirm(true, values.description);
            setConfirmModalVisible(false);
            confirmForm.resetFields();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    const getReportTemplates = async (studyId, requestId) => {
        const response = await ApiService.ReportTemplate.GetList(studyId, requestId);
        if (response.success) {
            return response.data.map(t => {
                return {
                    ...t,
                    value: t.id,
                    label: t.name
                }
            })
        }

        return []
    }

    const init = async () => {
        if (!request?.id)
            return
        await getReadUsers()
        const templates = await getReportTemplates(studyId, request?.id)
        setTemplates(templates)

        if (request?.reportId) {
            await loadReport(request?.id, request?.reportId)
        }
        else {
            let templateId = templates[0]?.id;
            await loadReportTemplateFile(templateId)
        }
    }

    useEffect(() => {
        init()
    }, [request])

    useEffect(() => {
        addKeyEvent([
            { keyCode: 'KeyQ', modifier: 'ControlLeft', prevent: false, },
            { keyCode: 'KeyQ', modifier: 'AltLeft', prevent: false, },
            // {keyCode: 'F4', modifier: 'ControlLeft', prevent: true,},
        ])

        return () => {
            removeKeyEvent([
                { keyCode: 'KeyQ', modifier: 'ControlLeft', prevent: false, },
                { keyCode: 'KeyQ', modifier: 'AltLeft', prevent: false, },
            ])
        }
    }, []);

    useEffect(() => {
        if (pressedKey?.key?.code === 'KeyQ' && pressedKey?.modifier?.code === 'ControlLeft') {
            // console.log('Pressed CRTL+KeyQ')
            handleSaveAndConfirmClick()
        }
        else if (pressedKey?.key?.code === 'KeyQ' && pressedKey?.modifier?.code === 'AltLeft') {
            runSave(false)
        }
    }, [pressedKey]);

    return (
        <>
            <Form
                ref={formRef}
                layout={'vertical'}
            >
                <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', paddingBottom: 8 }}>
                <Card size="small" style={{ marginBottom: 8, backgroundColor: '#e6f7ff', borderColor: '#1890ff' }} bodyStyle={{ padding: 8 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Space split={<Divider type="vertical" />} wrap={true}>
                            {request?.requestTypeName && (
                                <Typography.Text strong>{request?.requestTypeName}</Typography.Text>
                            )}
                            {request?.accessionNumber && (
                                <Typography.Text>{t('Accession No')}: {request.accessionNumber}</Typography.Text>
                            )}
                            {request?.requestDate && (
                                <Typography.Text>{t('Date')}: {new Date(request.requestDate).toLocaleDateString()}</Typography.Text>
                            )}
                            {request?.requestingPhysicianName && (
                                <Typography.Text>{t('Physician')}: {request.requestingPhysicianName}</Typography.Text>
                            )}
                        </Space>
                        <Space wrap={true}>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Select
                                    disabled={!request?.id || request?.reportIsConfirm}
                                    placeholder={t('Report Template')}
                                    value={template.value}
                                    style={{ minWidth: 200 }}
                                    options={templates}
                                    onChange={onTemplateChange}
                                />
                            </Form.Item>
                            <Form.Item
                                name={'studyReadUserId'}
                                required={true}
                                style={{ marginBottom: 0 }}
                            >
                                <Select
                                    disabled={!request?.id || request?.reportIsConfirm}
                                    style={{ width: 150 }}
                                    placeholder={t('Read User')}
                                >
                                    {
                                        readUsers.map(x => (
                                            <Select.Option key={x.id} value={x.id}>
                                                {x?.user?.visibleName}
                                            </Select.Option>
                                        ))
                                    }
                                </Select>
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                    disabled={!request?.id || request?.reportIsConfirm || confirming}
                                    loading={saving}
                                    onClick={async () => {
                                        await runSave(false)
                                    }}
                                    type={'primary'}>
                                    {t('Save') + ' (Alt + Q)'}
                                </Button>
                            </Form.Item>
                            {
                                hasPermission(PERMISSIONS.REPORT_CONFIRM) && (
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Button
                                            disabled={!request?.id || request?.reportIsConfirm || saving}
                                            loading={confirming}
                                            onClick={handleSaveAndConfirmClick}
                                        >
                                            {t('Save And Confirm') + ' (Ctrl + Q)'}
                                        </Button>
                                    </Form.Item>
                                )
                            }
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                    disabled={!request?.id}
                                    loading={previewing}
                                    onClick={() => runPreviewPdf()}
                                    type={'primary'}>
                                    {t('Preview')}
                                </Button>
                            </Form.Item>
                        </Space>
                    </div>
                </Card>

                {request?.studyTasks && request.studyTasks.length > 0 && !request?.reportIsConfirm && (
                    <div style={{ marginTop: 12, marginBottom: 12, padding: 12, backgroundColor: '#fff7e6', borderRadius: 4, border: '1px solid #ffd591' }}>
                        <strong style={{ color: '#d46b08' }}>{t('Open Tasks')}:</strong>
                        {request.studyTasks.map((task, idx) => (
                            <div key={idx} style={{ marginTop: 8 }}>
                                <div style={{ fontWeight: 500 }}>{t(studyTaskTypeKey(task.taskType))}</div>
                                {task.description && <div style={{ fontSize: 12, color: '#666' }}>{task.description}</div>}
                            </div>
                        ))}
                    </div>
                )}
                </div>

                <div
                    style={{
                        border: '1px solid #cfcfcf',
                        padding: 4
                    }}
                >
                        <ReportEditor
                            editable={request?.id}
                            defaultReportContent={request?.id ? reportContent : {}}
                            onBulgular={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    Bulgular: data
                                }
                            }}
                            onSonuc={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    SonucVeOneriler: data
                                }
                            }}
                            onKarsilastirma={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    Karsilastirma: data
                                }
                            }}
                            onTeknik={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    Teknik: data
                                }
                            }}
                        />
                </div>
            </Form>
            <Modal
                title={t('Confirm Report - Open Task Exists')}
                open={confirmModalVisible}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    confirmForm.resetFields();
                }}
                onOk={handleConfirmWithDescription}
                confirmLoading={confirming}
                okText={t('Confirm')}
                cancelText={t('Cancel')}
            >
                <OpenTasksBox tasks={request?.studyTasks} />
                <Form form={confirmForm} layout="vertical">
                    <TaskResolutionField tasks={request?.studyTasks} />
                </Form>
            </Modal>
        </>
    )
}
